import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Stack, Alert, CircularProgress, Card, CardContent } from '@mui/joy';
import { signIn, signOut, isSignedIn, getCurrentUserProfile, getMsalInstance } from '../utils/graphService';
import { graphConfig, graphScopes } from '../config/graphConfig';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useNavigate } from 'react-router-dom';



// Debug information
const debugInfo = {
  appId: graphConfig.appId,
  authority: graphConfig.authority,
  redirectUri: graphConfig.redirectUri,
  scopes: graphScopes
};

const AuthTest: React.FC = () => {
  const { user } = useSupabaseAuth();
  const navigate = useNavigate();
  const [authStatus, setAuthStatus] = useState<'checking' | 'signed-in' | 'signed-out' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [detailedError, setDetailedError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const profile = await getCurrentUserProfile();
      setUserProfile(profile);
      console.log("User profile:", profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
      setDetailedError(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
      setError("Failed to fetch user profile");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    try {
      setAuthStatus('checking');
      const signedIn = await isSignedIn();
      console.log("Auth status check - signed in:", signedIn);
      
      if (signedIn === true) {
        setAuthStatus('signed-in');
        await fetchUserProfile();
      } else {
        setAuthStatus('signed-out');
      }
    } catch (error) {
      console.error("Error checking auth status:", error);
      setAuthStatus('error');
    }
  }, [fetchUserProfile]);

  // Handle redirect response when component mounts
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        setAuthStatus('checking');
        const msalInstance = getMsalInstance();
        const response = await msalInstance.handleRedirectPromise();
        if (response && response.account) {
          msalInstance.setActiveAccount(response.account);
          console.log("Redirect was handled:", response);
        }
        await checkAuthStatus();
      } catch (error) {
        // Handle specific MSAL cache errors gracefully
        if (error instanceof Error && error.message.includes('no_token_request_cache_error')) {
          console.log("No token request in cache - this is normal when not coming from a redirect");
          await checkAuthStatus();
          return;
        }
        console.error("AuthTest: Error handling redirect:", error);
        setDetailedError(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
        await checkAuthStatus(); // Still try to check auth status
      }
    };
    handleRedirect();
  }, [checkAuthStatus]);

  const handleSignIn = async () => {
    try {
      setAuthStatus('checking');
      setError(null);
      setDetailedError(null);
      await signIn();
      // signIn will redirect, so we shouldn't get here
      checkAuthStatus();
    } catch (error) {
      console.error("Sign in error:", error);
      setDetailedError(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
      setError("Failed to sign in");
      setAuthStatus('error');
    }
  };

  const handleSignOut = async () => {
    try {
      setAuthStatus('checking');
      setError(null);
      setDetailedError(null);
      
      // Show a message that sign out is in progress
      console.log("Starting sign out process...");
      
      await signOut();
      // signOut will redirect, so we shouldn't get here
      console.log("Sign out completed without redirect");
      setAuthStatus('signed-out');
      setUserProfile(null);
    } catch (error) {
      console.error("Sign out error:", error);
      setDetailedError(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
      setError("Failed to sign out");
      setAuthStatus('error');
      
      // Even if sign out fails, try to clear local state
      try {
        const msalInstance = getMsalInstance();
        msalInstance.clearCache();
        setAuthStatus('signed-out');
        setUserProfile(null);
      } catch (clearError) {
        console.error("Failed to clear cache:", clearError);
      }
    }
  };

  // Direct MSAL login for testing - uses the same instance as the main app
  const handleDirectLogin = async () => {
    try {
      setError(null);
      setDetailedError(null);
      
      // Use the main MSAL instance instead of creating a separate one
      const msalInstance = getMsalInstance();
      
      // Check if already signed in
      const currentAccounts = msalInstance.getAllAccounts();
      if (currentAccounts.length > 0) {
        console.log("Already signed in, setting active account");
        msalInstance.setActiveAccount(currentAccounts[0]);
        await checkAuthStatus();
        return;
      }
      
      // Try login with popup instead of redirect to avoid interaction conflicts
      const loginResponse = await msalInstance.loginPopup({
        scopes: graphScopes,
        prompt: 'select_account'
      });
      
      if (loginResponse.account) {
        msalInstance.setActiveAccount(loginResponse.account);
        console.log("Direct login successful:", loginResponse.account);
        await checkAuthStatus();
      }
    } catch (error) {
      console.error("Direct login error:", error);
      setDetailedError(error instanceof Error ? `${error.name}: ${error.message}\n${error.stack}` : String(error));
      setError("Failed to perform direct login");
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography level="h2" mb={3}>Microsoft Graph Authentication Test</Typography>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography level="h3" mb={2}>Current Configuration</Typography>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowX: 'auto', p: 2, bgcolor: 'background.level1', borderRadius: 'sm', fontSize: 'sm' }}>
            {JSON.stringify(debugInfo, null, 2)}
          </Box>
        </CardContent>
      </Card>
      
      {authStatus === 'checking' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
          {detailedError && (
            <Box component="pre" sx={{ mt: 2, p: 2, bgcolor: 'danger.softBg', borderRadius: 'sm', fontSize: 'xs', whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
              {detailedError}
            </Box>
          )}
          {detailedError && detailedError.includes('Content Security Policy') && (
            <Typography level="body-sm" mt={2} color="neutral">
              Note: The CSP error above is normal when signing out from Microsoft services. The sign out process should still complete successfully.
            </Typography>
          )}
        </Alert>
      )}
      
      {authStatus === 'signed-in' && !userProfile && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
          <Typography ml={2}>Loading profile...</Typography>
        </Box>
      )}
      
      {authStatus === 'signed-in' && userProfile && (
        <Box sx={{ mb: 3 }}>
          <Alert color="success" sx={{ mb: 2 }}>
            You are signed in
          </Alert>
          
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'neutral.outlinedBorder', borderRadius: 'sm' }}>
            <Typography level="h4" mb={1}>{userProfile.displayName}</Typography>
            <Typography>{userProfile.userPrincipalName || userProfile.mail}</Typography>
            <Typography level="body-sm" mt={1}>ID: {userProfile.id}</Typography>
          </Box>
        </Box>
      )}
      
      {authStatus === 'signed-out' && (
        <Alert color="neutral" sx={{ mb: 3 }}>
          You are not signed in
        </Alert>
      )}
      
      <Stack direction="row" spacing={2} justifyContent="center" mb={4}>
        <Button 
          onClick={handleSignIn} 
          disabled={authStatus === 'checking' || authStatus === 'signed-in'}
          color="primary"
        >
          Sign In
        </Button>
        
        <Button 
          onClick={handleSignOut}
          disabled={authStatus === 'checking' || authStatus === 'signed-out'}
          color="neutral"
        >
          Sign Out
        </Button>
      </Stack>
      
      <Card>
        <CardContent>
          <Typography level="h3" mb={2}>Alternative Login Method</Typography>
          <Typography mb={2}>Use this if the regular sign-in button doesn't work. This uses a popup login instead of redirect.</Typography>
          <Button onClick={handleDirectLogin} color="primary" variant="solid">
            Login with Popup
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthTest; 